/**
 * Facebook Authentication Service
 * Handles Facebook SDK initialization and login popup
 */

// Facebook App Configuration
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || 'YOUR_FACEBOOK_APP_ID';
const FACEBOOK_API_VERSION = process.env.REACT_APP_FACEBOOK_API_VERSION || 'v18.0';

// SDK loading state
let sdkLoaded = false;
let sdkLoadingPromise = null;

/**
 * Load Facebook SDK asynchronously
 * @returns {Promise<void>}
 */
export const loadFacebookSDK = () => {
  // If already loading, return existing promise
  if (sdkLoadingPromise) {
    return sdkLoadingPromise;
  }

  // If already loaded, return resolved promise
  if (sdkLoaded) {
    return Promise.resolve();
  }

  sdkLoadingPromise = new Promise((resolve, reject) => {
    // Check if SDK script already exists
    if (document.getElementById('facebook-jssdk')) {
      sdkLoaded = true;
      resolve();
      return;
    }

    // Check if App ID is configured
    if (!FACEBOOK_APP_ID || FACEBOOK_APP_ID === 'YOUR_FACEBOOK_APP_ID') {
      console.error('❌ Facebook App ID not configured. Please add REACT_APP_FACEBOOK_APP_ID to your .env file');
      reject(new Error('Facebook App ID not configured'));
      return;
    }

    // Load Facebook SDK
    window.fbAsyncInit = function () {
      try {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: true,
          xfbml: false, // Disable XFBML parsing to avoid additional requests
          version: FACEBOOK_API_VERSION,
          autoLogAppEvents: false, // Disable automatic event logging
          frictionlessRequests: false,
        });

        console.log('✅ Facebook SDK initialized');
        sdkLoaded = true;
        resolve();
      } catch (error) {
        console.error('❌ Error initializing Facebook SDK:', error);
        sdkLoadingPromise = null;
        reject(error);
      }
    };

    // Insert SDK script
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = `https://connect.facebook.net/en_US/sdk.js`;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';

    script.onerror = () => {
      console.error('❌ Failed to load Facebook SDK - Check network or ad blockers');
      sdkLoadingPromise = null;
      reject(new Error('Failed to load Facebook SDK'));
    };

    // Append script to body
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  });

  return sdkLoadingPromise;
};

/**
 * Open Facebook login popup and get user data
 * @returns {Promise<Object>} User data from Facebook
 */
export const loginWithFacebook = async () => {
  try {

    // Ensure SDK is loaded
    await loadFacebookSDK();

    if (!window.FB) {
      throw new Error('Facebook SDK not loaded');
    }


    // Open Facebook login popup
    return new Promise((resolve, reject) => {
      try {
        window.FB.login(
          (response) => {

            if (response.status === 'connected') {

              // Get user profile data
              window.FB.api('/me', { fields: 'id,name,email,picture' }, (userInfo) => {
                

                if (userInfo && !userInfo.error) {
                  resolve({
                    provider_id: userInfo.id,
                    name: userInfo.name,
                    email: userInfo.email || null,
                    picture: userInfo.picture?.data?.url || null,
                    accessToken: response.authResponse.accessToken,
                  });
                } else {
                  console.error('❌ Error fetching user info:', userInfo.error);
                  reject(new Error(userInfo.error?.message || 'Failed to fetch user info'));
                }
              });
            } else if (response.status === 'not_authorized') {
              console.warn('⚠️ User cancelled login or did not authorize');
              reject(new Error('User cancelled login or did not authorize'));
            } else if (response.status === 'unknown') {
              console.warn('⚠️ User closed the popup or blocked the login');
              reject(new Error('Login cancelled'));
            } else {
              console.warn('⚠️ Unexpected response status:', response.status);
              reject(new Error('Login failed'));
            }
          },
          {
            scope: 'public_profile,email',
            return_scopes: true,
            auth_type: 'rerequest', // Ask for denied permissions again
          }
        );
      } catch (fbError) {
        console.error('❌ Facebook SDK error:', fbError);
        // Check if it's the "Failed to fetch" error
        if (fbError.message && fbError.message.includes('Failed to fetch')) {
          reject(new Error('Unable to connect to Facebook. Please check your internet connection or try again later.'));
        } else {
          reject(new Error('Facebook login failed. Please try again.'));
        }
      }
    });
  } catch (error) {
    console.error('❌ Facebook login error:', error);

    // Provide user-friendly error messages
    if (error.message.includes('Facebook App ID not configured')) {
      throw new Error('Facebook login is not configured. Please contact support.');
    } else if (error.message.includes('Failed to load Facebook SDK')) {
      throw new Error('Unable to load Facebook login. Please check your internet connection or disable ad blockers.');
    } else {
      throw error;
    }
  }
};

/**
 * Logout from Facebook
 * @returns {Promise<void>}
 */
export const logoutFromFacebook = async () => {
  try {
    await loadFacebookSDK();

    if (!window.FB) {
      console.warn('Facebook SDK not loaded');
      return;
    }

    return new Promise((resolve) => {
      window.FB.logout((response) => {
        console.log('Facebook logout response:', response);
        resolve();
      });
    });
  } catch (error) {
    console.error('Error logging out from Facebook:', error);
  }
};

/**
 * Get Facebook login status
 * @returns {Promise<Object>}
 */
export const getFacebookLoginStatus = async () => {
  try {
    await loadFacebookSDK();

    if (!window.FB) {
      return { status: 'unknown' };
    }

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response) => {
        resolve(response);
      });
    });
  } catch (error) {
    console.error('Error getting Facebook login status:', error);
    return { status: 'unknown' };
  }
};

export default {
  loadFacebookSDK,
  loginWithFacebook,
  logoutFromFacebook,
  getFacebookLoginStatus,
};
